/** *************************************************************
 * Copyright (c) 2006 - 2020, Case Anywhere, LLC.
 * All rights reserved
 *
 * @author Brad Wetmore
 * Date: 01/18/2021
 *
 * @version 0.2
 * Revisions:
 * 01/18/2021 - BW - Initial Creation
 * 07/22/2021 - BW - Modified: processPaymentProfileAjax - adjusted error catch to
 *                   use:"processErrorWithModalFailAlertWithNotifiedMessage" instead
 *                   Modified: processSave - adjusted error catch to
 *                   use:"processErrorWithModalFailAlertWithNotifiedMessage" instead
 *
 * Description/Purpose:
 * Payment Profile
 ************************************************************** */
import AjaxUtil from "util/ajax-util";
import ModalUtil from "util/modal-util";
import StackTraceError from "component/stack-trace-error/stack-trace-error";
import BannerUtil from "component/banner/banner-util";
import { TAG_DATA as PAYMENT_TAG_DATA } from "page/payment/payment-util";
import {
  COMMON, PaymentProfileUtil, TAG_DATA, VALIDATION_MESSAGE,
} from "page/payment/payment-profile-util";
import PaymentProfileAjaxResponseJsonDTO from "dto/payment-profile-ajax-response-json-dto";
import DomUtils from "util/dom-util";
import { COMMON_CLASS, TINGLE } from "constant/app-constant";
import Modal from "component/modal/modal";
import WindowModal from "component/modal/window-modal";
import CommonUtil from "util/common-util";
import ButtonUtil from "util/button-util";
import PaymentProfileDTO from "dto/payment-profile-dto";
import BasicAjaxResponseJSONDTO from "dto/basic-ajax-response-json-dto";
import AjaxResponseUtil from "util/ajax-response-util";
import ValidateForm from "util/validate-form";
import ConfirmModal from "component/modal/confirm-modal";
import PaymentProfileExpirationDate from "./payment-profile-expiration-date";
import { ValidateFormUtil } from "../../util/validate-form-util";


export default class PaymentProfile {
  /**
     *
     * @param $form
     * @param options
     * @returns {FileUploadUpload}
     */
  constructor($form, options = []) {
    this._$form = $form;

    const defaults = {
      banner: 'payment_profile_banner',
      noRecordsText: 'There Is No Payment Profile',
      // $button: '',
      modalHeader: 'Case Anywhere Payment Profile',
      // eAct: '',
    };
    this.opts = $.extend(true, defaults, options);

    this.setUpVariable();
    this.cacheDom();
    this.registerEvent();

    return this;
  }


  /**
     * Setup some common vars
     */
  setUpVariable() {
    return this;
  }


  /**
     * Caches some often used DOM objects
     * @returns {ForgotCredential}
     */
  cacheDom() {
    this._$paymentProfileButton = $(COMMON.PAYMENT_PROFILE_BUTTON_SELECTOR);
    this._$paymentProfileButtonContainer = $(COMMON.PAYMENT_PROFILE_BUTTON_SELECTOR).parent();
  }


  /**
     * Caches modal elements
     * @returns {ForgotCredential}
     * @private
     */
  modalCacheDom() {
    try {
      this._modalClassSelector = `.${TINGLE.MODAL}`;
      this._$modalForm = $(COMMON.PAYMENT_PROFILE_FORM_SELECTOR);
      this._$modalFormContent = $(COMMON.PAYMENT_PROFILE_MODAL_FORM_CONTENT_SELECTOR);
      this._$modalUL = $(COMMON.PAYMENT_PROFILE_MODAL_UL_SELECTOR);
      this._$modalLoadingBanner = $(COMMON.PAYMENT_PROFILE_MODAL_LOADING_BANNER_SELECTOR);
      this._$modalSaveButton = $(COMMON.PAYMENT_PROFILE_MODAL_SAVE_BUTTON_SELECTOR);
      this._$modalCancelButton = $(COMMON.PAYMENT_PROFILE_MODAL_CANCEL_BUTTON_SELECTOR);
      this._$paymentProfileList = $(COMMON.PAYMENT_PROFILE_MODAL_PAYMENT_PROFILE_LIST_SELECTOR);
    } catch (e) {
      StackTraceError.processError(e, 'Payment Profile - Modal Cache Dom');
    }
  }


  /**
     * Register events or DOM event
     * @private
     */
  registerEvent() {
    try {
      this._$paymentProfileButton
        .off('click')
        .on('click',
          ($event) => this.onDisplayPaymentProfilePageClick($event));
    } catch (e) {
      StackTraceError.processError(e, 'Payment Profile - Register Event');
    }
  }


  /**
     * Register modal events for modal
     * @private
     */
  registerModalEvent() {
    try {
      this._registerModalValidateForm();
      this._$modalForm.on('click', `${COMMON.PAYMENT_PROFILE_MODAL_DELETE_ICON_CLASS}`,
        ($event) => this.onModalDeleteIconClick($event));
      this._$modalCancelButton
        .off('click')
        .on('click',
          ($event) => this.onModalCancelButtonClick($event));
      this._$modalSaveButton
        .off('click')
        .on('click',
          ($event) => this.onModalSaveButtonClick($event));
    } catch (e) {
      StackTraceError.processError(e, 'Payment Profile - Register Modal Event');
    }
  }


  /**
     * Register the ValidateForm Event and setup the object
     * @private
     */
  _registerModalValidateForm() {
    try {
      this.validateForm = new ValidateForm(this._$modalForm,
        {
          errorBanner: '#payment_profile_error_banner',
          errorBannerList: '#payment_profile_error_banner_list',
          addErrorBannerParent: this._$modalForm,
        })
        .init();
      ValidateFormUtil.addCreditCardExpirationDateExpired();
      ValidateFormUtil.addCreditCardExpirationDateFormat();
    } catch (e) {
      StackTraceError.processError(e, 'Message Board - Register Validate Form');
    }
  }


  /**
     * Will open and display the "Payment Profile" Modal Window
     * @param $event
     * @private
     */
  onDisplayPaymentProfilePageClick($event) {
    try {
      $event.preventDefault();
      $event.stopPropagation();

      // // Update some Forgot Credential modal data
      this._modal = new WindowModal({
        closeMethods: [],
        cssClass: [COMMON.PAYMENT_PROFILE_MODAL_CLASS],
      })
        .addHeader(this.opts.modalHeader,
          false)
        .addCustomIcon()
        .setContent($(`${COMMON.PAYMENT_PROFILE_FORM_SELECTOR}${Modal.modalSuffix}`)
          .prop('outerHTML')); // don't use cacheDOM
      // if (this.opts.addFocusToFirstElement) this._modal.addFocusToFirstElement();
      this._modal.open();

      // Set up the Dom cache for the modal
      this.modalCacheDom();

      // Now register the event for the modal
      this.registerModalEvent();

      // Process the ajax
      this.processPaymentProfileAjax();
    } catch (e) {
      StackTraceError.processError(e, 'Payment Profile Modal');
    }
  }


  /**
     * Will open and display the "Payment Profile" Modal Window
     * @param $event
     * @private
     */
  processPaymentProfileAjax() {
    try {
      //  Send ajax call to get the total row count data
      const request = AjaxUtil.getJsonToAjaxServlet(
        PAYMENT_TAG_DATA.URL_PAYMENT_SERVLET,
        TAG_DATA.E_ACT_DISPLAY_PAYMENT_PROFILE_PAGE,
        null, {},
      );
      request.done((data) => {
        // Render and then un-hide content and then remove the banner
        this.render(data, this._$modalUL);

        // Init the PaymentProfileExpirationDate
        this._$modalForm.find(`.${COMMON.PAYMENT_PROFILE_MODAL_EXPIRES_INPUT}`)
          .onlyDateNumbers();
        this._paymentProfileExpirationDate = PaymentProfileExpirationDate.init(this._$modalForm);

        // Add Validation
        this.addModalValidation();

        // Remove Loading Banner
        BannerUtil.hideBanner(COMMON.PAYMENT_PROFILE_MODAL_LOADING_BANNER_SELECTOR);

        // Show the content
        CommonUtil.show(this._$modalFormContent);

        // Check Overflow
        ModalUtil.checkOverflow(this._modal);
      });
      request.fail((jqXHR, textStatus, errorThrown) => {
        StackTraceError.processErrorWithModalFailAlertWithNotifiedMessage(
          new Error(errorThrown), 'Payment Profile Modal Ajax Fail',
        );
        // ModalUtil.showModalFailAlert();
        // StackTraceError.processError(new Error(errorThrown), 'Payment Profile Modal Ajax Fail');
      });
    } catch (e) {
      StackTraceError.processError(e, 'Payment Profile Modal');
    }
  }


  /**
     * Will render the data to the DOM
     * @param date
     * @param $modalContent
     */
  render(data, $modalContent) {
    const paymentProfileAjaxResponseJsonDTO = new PaymentProfileAjaxResponseJsonDTO(data);
    console.log(paymentProfileAjaxResponseJsonDTO);
    // Else we know there are records - so process them and render to the DOM
    let frag = document.createDocumentFragment();

    // // There are records
    if (paymentProfileAjaxResponseJsonDTO.totalRowCount > 0) {
      frag = this._renderPaymentProfileList(
        paymentProfileAjaxResponseJsonDTO.customerPaymentProfileMaskedTypeList,
      );
    } else {
      frag = DomUtils.render(this.opts.noRecordsText);
    }

    // Render frag to DOM
    $modalContent[0].appendChild(frag);
  }


  /**
     * Will render the PaymentProfileList list data to the DOM
     * @param paymentProfileList
     * @returns {DocumentFragment}
     */
  _renderPaymentProfileList(paymentProfileList) {
    this.__renderPaymentProfileList = true; // eslint
    // Else we know there are records - so process them and render to the DOM
    const frag = document.createDocumentFragment();
    const mainUl = document.createElement("ul");
    mainUl.className = `payment-profile-container`;

    // Process each paymentProfile (from Auth.net)
    paymentProfileList.forEach((dto, index) => {
      const { creditCard } = dto.payment;
      const expiresDateArr = creditCard.expirationDate.split('-');
      const expiresDate = `${expiresDateArr[1]}/${expiresDateArr[0].substring(2, 4)}`;

      // Create an li for each record
      const paymentProfileLi = document.createElement("li");
      paymentProfileLi.className = 'payment-profile-container-item';
      paymentProfileLi.id = dto.customerPaymentProfileId;

      // Create Delete button
      const deleteColumn = document.createElement("div");
      deleteColumn.className = 'delete-column';
      const paymentLineDeleteIcon = document.createElement("i");
      paymentLineDeleteIcon.className = 'delete-icon';
      // Append
      deleteColumn.appendChild(paymentLineDeleteIcon);

      // CC icon
      const creditCardClass = creditCard.cardType.toLowerCase();
      const creditCardColumn = document.createElement("div");
      creditCardColumn.className = 'credit-card-column';
      // Credit Card Icon
      const creditCardIcon = document.createElement("i");
      creditCardIcon.className = `credit-card-icon ${creditCardClass}`;
      // Hidden Full Credit Card Input
      const creditCardInput = document.createElement("input");
      creditCardInput.type = "hidden";
      creditCardInput.className = `text ${COMMON.PAYMENT_PROFILE_MODAL_CREDIT_CARD_INPUT}`;
      creditCardInput.value = creditCard.cardNumber;
      // Credit Card Text
      const creditCardTextContainer = document.createElement("span");
      creditCardTextContainer.className = COMMON.PAYMENT_PROFILE_MODAL_CREDIT_CARD_TEXT_CONTAINER_CLASS;
      const creditCardText = document.createTextNode(`ending in ${creditCard.cardNumber.substring(4, 8)}`);
      creditCardTextContainer.appendChild(creditCardText);
      // Append
      creditCardColumn.appendChild(creditCardIcon);
      creditCardColumn.appendChild(creditCardInput);
      creditCardColumn.appendChild(creditCardTextContainer);

      // GAP
      const gapColumn = document.createElement("div");
      gapColumn.className = 'gap-column';

      // Expires
      const expiresContainer = document.createElement("div");
      expiresContainer.className = 'expires-column';
      // Label
      const expiresLabel = document.createElement("label");
      expiresLabel.className = COMMON_CLASS.COLON_REQUIRED;
      const expiresLabelText = document.createTextNode("Expires");
      expiresLabel.appendChild(expiresLabelText);

      const expiresFormatLabel = document.createElement("label");
      expiresFormatLabel.className = 'expires-format';
      const expiresFormatText = document.createTextNode("(mm/yy)");
      expiresFormatLabel.appendChild(expiresFormatText);

      const expiresLabelContainer = document.createElement("div");
      expiresLabelContainer.className = 'expires-label-container';
      expiresLabelContainer.appendChild(expiresLabel);
      expiresLabelContainer.appendChild(expiresFormatLabel);
      // Input
      const expiresInput = document.createElement("input");
      expiresInput.id = `${COMMON.PAYMENT_PROFILE_MODAL_EXPIRES_INPUT_ID_PREFIX}${index}`;
      expiresInput.name = `${COMMON.PAYMENT_PROFILE_MODAL_EXPIRES_INPUT_ID_PREFIX}${index}`;
      expiresInput.type = "text";
      expiresInput.className = `text ${COMMON.PAYMENT_PROFILE_MODAL_EXPIRES_INPUT}`;
      expiresInput.autocomplete = 'off';
      expiresInput.autocorrect = 'off';
      expiresInput.autocapitalize = 'off';
      expiresInput.maxLength = "5";
      // expiresInput.placeholder = "mm/yy";
      expiresInput.value = expiresDate;
      // Append
      expiresContainer.appendChild(expiresLabelContainer);
      expiresContainer.appendChild(expiresInput);

      // Append Elements
      paymentProfileLi.appendChild(deleteColumn);
      paymentProfileLi.appendChild(creditCardColumn);
      paymentProfileLi.appendChild(gapColumn);
      paymentProfileLi.appendChild(expiresContainer);

      // LI
      mainUl.appendChild(paymentProfileLi);
    });

    frag.appendChild(mainUl);

    // RETURN frag to DOM
    return frag;
  }


  /**
     * Will add the dynamic modal validation
     */
  addModalValidation() {
    let groupTitleSSV = '';
    const titleRuleObj = {};
    const titleMessageObj = {};
    $(`${COMMON.PAYMENT_PROFILE_MODAL_PAYMENT_PROFILE_CONTAINER_ITEM_CLASS} `
            + `.${COMMON.PAYMENT_PROFILE_MODAL_EXPIRES_INPUT}`)
      .each((index, value) => {
        const $input = $(value);
        const name = $input.attr('name');

        // Process the group data first
        groupTitleSSV += groupTitleSSV.length > 0 ? ` ${name}` : name;

        // Next add the rules for each element
        titleRuleObj[name] = {
          required: true,
          creditCardExpirationDateFormat: true,
          creditCardExpirationDateExpired: true,
        };

        // Finally add the messages for each element
        titleMessageObj[name] = {
          required: VALIDATION_MESSAGE.EXPIRES_REQUIRED,
          creditCardExpirationDateFormat: VALIDATION_MESSAGE.EXPIRES_FORMAT,
          creditCardExpirationDateExpired: VALIDATION_MESSAGE.EXPIRES_DATE_EXPIRED,
        };
      });
    // Add to the ValidationForm
    this.validateForm
      .addGroups({ titleGroup: groupTitleSSV })
      .addRules(titleRuleObj)
      .addMessages(titleMessageObj);
    this.validateForm.init().checkValidate();
  }


  /**
     * Will process a "delete".  This will remove the payment profile row
     * @param $event
     */
  onModalDeleteIconClick($event) {
    const result = false;
    try {
      if ($event) $event.preventDefault();

      // Figure out the header
      const header = `${this.opts.modalHeader} Delete`;
      const $this = $($event.currentTarget);
      const $parent = PaymentProfileUtil.getPaymentProfileItemParent($this);
      const creditCardText = $parent.find(`.${COMMON.PAYMENT_PROFILE_MODAL_CREDIT_CARD_TEXT_CONTAINER_CLASS}`);

      // Display the Confirm Modal
      const confirmationModal = new ConfirmModal({
        cssClass: ['payment-profile-delete-confirmation-modal', 'modal-max-width'],
      })
        .addHeader(header)
        .addCustomIcon()
        .setContent(`Are you sure you want to delete this payment profile ${creditCardText.html()}?`)
        .addPrimaryFooterButton(Modal.primaryFooterButtonLabel,
          Modal.primaryFooterButtonWithOkCssClass,
          () => {
            confirmationModal.close();
            this.processDelete($event);
          })
        .open();
    } catch (e) {
      StackTraceError.processError(e, 'Message Board - Post Message - Modal Save Button Click');
    }
    return result;
  }


  /**
     * Will process a "delete".  This will remove the payment profile row
     * @param $event
     */
  processDelete($event) {
    try {
      const $this = $($event.currentTarget);
      const $parent = PaymentProfileUtil.getPaymentProfileItemParent($this);
      CommonUtil.hideClass($parent);
      ModalUtil.checkOverflow(this._modal);
    } catch (e) {
      StackTraceError.processError(e, 'Payment Profile - Delete');
    }
  }


  /**
     * Processes the modal "Save" button
     * @param $event
     */
  onModalSaveButtonClick($event) {
    let result = false;
    try {
      if ($event) $event.preventDefault();

      // Validate
      result = this._$modalForm.valid();
      if (!result) return false;

      // Figure out the header
      const header = `${this.opts.modalHeader} Confirmation`;

      // Display the Confirm Modal
      const confirmationModal = new ConfirmModal({
        cssClass: ['payment-profile-save-confirmation-modal', 'modal-max-width'],
      })
        .addHeader(header)
        .addCustomIcon()
        .setContent("Are you sure you want to save this payment profile data?")
        .addPrimaryFooterButton(Modal.primaryFooterButtonLabel,
          Modal.primaryFooterButtonWithOkCssClass,
          () => {
            confirmationModal.close();
            this.processSave($event);
          })
        .open();
    } catch (e) {
      StackTraceError.processError(e, 'Message Board - Post Message - Modal Save Button Click');
    }
    return result;
  }


  /**
     * Processes the modal "Save" button
     * @param $event
     */
  processSave($event) {
    let result = false;
    try {
      // Get the Payment Profile Object list
      this._$paymentProfileList.val(this.getPaymentProfileList());

      ButtonUtil.enableProcessingButton(this._$modalSaveButton, true, this._modalClassSelector);

      //  Send ajax call to get the total row count data
      const request = AjaxUtil.postJsonToAjaxServlet(
        PAYMENT_TAG_DATA.URL_PAYMENT_SERVLET,
        PAYMENT_TAG_DATA.E_ACT_SAVE_PAYMENT_PROFILE_PAGE,
        this._$modalForm.serialize(), {},
      );
      request.done((data) => {
        const basicAjaxResponseJSONDTO = new BasicAjaxResponseJSONDTO(data);

        if (AjaxResponseUtil.isBasicAjaxResponseSuccess(basicAjaxResponseJSONDTO)) {
          ButtonUtil.disableProcessingButton(
            this._$modalSaveButton, true, false, this._modalClassSelector,
          );

          // Close the modal and show a basic alert
          this._modal.close();

          // May need to hide the  "Manage Payment Profile"
          if (basicAjaxResponseJSONDTO.totalRowCount <= 0) {
            this._$paymentProfileButtonContainer.addClass(COMMON_CLASS.ONE_BUTTON_BUTTON_CONTAINER_CLASS);
            CommonUtil.hide(this._$paymentProfileButton);
            this._$paymentProfileButton.removeClass(COMMON.PAYMENT_PROFILE_BUTTON_CLASS);
          }
          result = true;
          ModalUtil.showSuccessAlertMaxWidth(
            this.opts.modalHeader, 'The payment profile has been updated successfully',
          );
        } else if (AjaxResponseUtil.isBasicAjaxResponseError(basicAjaxResponseJSONDTO)) {
          ModalUtil.showModalFailAlert();
          ButtonUtil.disableProcessingButton(
            this._$modalSaveButton, true, false, this._modalClassSelector,
          );
        }
      });
      request.fail((jqXHR, textStatus, errorThrown) => {
        StackTraceError.processErrorWithModalFailAlertWithNotifiedMessage(
          new Error(errorThrown), 'Payment Profile - Save Ajax Fail',
        );
        // ModalUtil.showModalFailAlert();
        ButtonUtil.disableProcessingButton(
          this._$modalSaveButton, true, false, this._modalClassSelector,
        );
        // StackTraceError.processError(new Error(errorThrown), 'Payment Profile - Save Ajax Fail');
      });
    } catch (e) {
      ButtonUtil.disableProcessingButton(
        this._$modalSaveButton, true, false, this._modalClassSelector,
      );
      StackTraceError.processError(e, 'Payment Profile - Save - Process Save');
    }
    return result;
  }


  /**
     * Will get the PaymentProfile object
     * @returns
     */
  getPaymentProfileList() {
    this._getPaymentProfileList = true; // eslint
    // Loop through each customer payment profile and create a PaymentProfile object
    // with each and send back an array
    const paymentProfileArr = [];

    $(`${COMMON.PAYMENT_PROFILE_MODAL_PAYMENT_PROFILE_CONTAINER_ITEM_CLASS}:not(.${COMMON_CLASS.HIDE})`)
      .each(function processIt(index, value) {
        const $value = $(value);
        const id = $value.attr("id");
        const creditCardText = $(this).find(`.${COMMON.PAYMENT_PROFILE_MODAL_CREDIT_CARD_INPUT}`).val();
        const expirationDateText = $(this).find(`.${COMMON.PAYMENT_PROFILE_MODAL_EXPIRES_INPUT}`).val();

        const paymentProfile = new PaymentProfileDTO(id, creditCardText, expirationDateText);
        paymentProfileArr[index] = paymentProfile;
      });
    return JSON.stringify(paymentProfileArr);
  }


  /**
     * Processes the modal "Cancel" button
     * @param $event
     */
  onModalCancelButtonClick($event) {
    this._modal.close();
  }
}
